/// <reference types="vite/client" />

import {
  createRtkFetchSideLaneMiddleware,
  createRtkFetchSideLaneProxy,
  defineRtkFetchSideLanePolicy,
  rtkFetchSideLaneReducer,
  type RtkFetchSideLaneApiAdapter,
  type RtkFetchSideLaneMetricEvent,
  type RtkFetchSideLaneMode,
  type RtkFetchSideLaneOperationMatcher,
  type RtkFetchSideLaneRouteContext,
} from '@triedge/rtk-abstractions';
import { domainRouterApi } from '../domainRouterApi';
import {
  domainRouterUniversalApi,
  type DomainQueryRequest,
} from '../domainRouterUniversalApi';

type StoreLike = {
  getState: () => unknown;
};

type WindowWithFetchSideLaneEvents = Window & {
  __YSTNJ_RTK_FETCH_SIDE_LANE_EVENTS__?: RtkFetchSideLaneMetricEvent[];
};

const modeValues: readonly RtkFetchSideLaneMode[] = ['off', 'shadow', 'seed'];

const resolveMode = (): RtkFetchSideLaneMode => {
  const rawMode = String(
    import.meta.env.VITE_YSTNJ_RTK_FETCH_SIDE_LANE_MODE ?? 'off',
  ).trim();
  return modeValues.includes(rawMode as RtkFetchSideLaneMode)
    ? rawMode as RtkFetchSideLaneMode
    : 'off';
};

const recordEvent = (event: RtkFetchSideLaneMetricEvent) => {
  if (typeof window === 'undefined') return;
  const target = window as WindowWithFetchSideLaneEvents;
  target.__YSTNJ_RTK_FETCH_SIDE_LANE_EVENTS__ = [
    ...(target.__YSTNJ_RTK_FETCH_SIDE_LANE_EVENTS__ ?? []),
    event,
  ].slice(-1000);
};

const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, '') || '/';

export const resolveYstnjRtkFetchSideLaneRoute = (): RtkFetchSideLaneRouteContext | null => {
  if (typeof window === 'undefined') return null;
  const pathname = normalizePathname(window.location.pathname);

  if (pathname === '/school/admissions/dashboard') {
    return {
      routeId: 'admissions-staff-dashboard',
      routeFamily: 'admissions',
      pathname,
      personaScope: 'staff',
    };
  }

  if (/^\/school\/admissions\/(?:children|family|documents|notes|tuition)\/[^/]+$/.test(pathname)) {
    return {
      routeId: 'admissions-staff-detail',
      routeFamily: 'admissions',
      pathname,
      personaScope: 'staff',
    };
  }

  if (pathname === '/school/financialaid/staff') {
    return {
      routeId: 'financial-aid-staff-dashboard',
      routeFamily: 'financialAid',
      pathname,
      personaScope: 'staff',
    };
  }

  if (/^\/school\/financialaid\/staff\/[^/]+(?:\/[^/]+)?$/.test(pathname)) {
    return {
      routeId: 'financial-aid-staff-detail',
      routeFamily: 'financialAid',
      pathname,
      personaScope: 'staff',
    };
  }

  if (pathname === '/school/tuition') {
    return {
      routeId: 'tuition-staff-dashboard',
      routeFamily: 'tuition',
      pathname,
      personaScope: 'staff',
    };
  }

  if (/^\/school\/tuition\/family\/[^/]+$/.test(pathname)) {
    return {
      routeId: 'tuition-staff-family-ledger',
      routeFamily: 'tuition',
      pathname,
      personaScope: 'staff',
    };
  }

  return null;
};

const appEndpoint = (
  endpointName: string,
  facet: string,
): RtkFetchSideLaneOperationMatcher => ({
  apiId: domainRouterApi.reducerPath,
  endpointName,
  facet,
});

const universalQuery = (
  operation: string,
  facet: string,
): RtkFetchSideLaneOperationMatcher => ({
  apiId: domainRouterUniversalApi.reducerPath,
  endpointName: 'domainQuery',
  operation,
  facet,
});

export const ystnjRtkFetchSideLanePolicies = [
  defineRtkFetchSideLanePolicy({
    policyId: 'ystnj-admissions-staff-rtk-fetch-side-lane-v1',
    routeFamily: 'admissions',
    staffRoutes: [
      { routeId: 'admissions-staff-dashboard' },
      { routeId: 'admissions-staff-detail' },
      { pattern: '/school/admissions/dashboard' },
      { pattern: '/school/admissions/{children|family|documents|notes|tuition}/:id' },
    ],
    observedOperations: [
      universalQuery('listApplications', 'dashboard.applications'),
      appEndpoint('listAdmissionsApplications', 'dashboard.applications'),
      appEndpoint('getAdmissionsApplication', 'detail.application'),
      appEndpoint('getAdmissionsDocuments', 'detail.documents'),
      appEndpoint('getAdmissionsTimeline', 'detail.timeline'),
      appEndpoint('listFacultyMembers', 'detail.faculty'),
      appEndpoint('getSettingsModuleConfig', 'detail.settings'),
      universalQuery('resolveFamilyForAdmissionsApplication', 'detail.familyResolution'),
      universalQuery('getAdmissionsTuitionConfiguration', 'detail.tuitionConfiguration'),
    ],
    treeProjector: ({ read }) => ({
      staff: {
        dashboard: {
          get applications() {
            return read(appEndpoint('listAdmissionsApplications', 'dashboard.applications'), undefined);
          },
        },
        detail: new Proxy({}, {
          get(_target, property) {
            const applicationId = String(property);
            return {
              get application() {
                return read(appEndpoint('getAdmissionsApplication', 'detail.application'), applicationId);
              },
              get documents() {
                return read(appEndpoint('getAdmissionsDocuments', 'detail.documents'), { applicationId });
              },
              get timeline() {
                return read(appEndpoint('getAdmissionsTimeline', 'detail.timeline'), { applicationId });
              },
            };
          },
        }),
      },
    }),
  }),
  defineRtkFetchSideLanePolicy({
    policyId: 'ystnj-financial-aid-staff-rtk-fetch-side-lane-v1',
    routeFamily: 'financialAid',
    staffRoutes: [
      { routeId: 'financial-aid-staff-dashboard' },
      { routeId: 'financial-aid-staff-detail' },
      { pattern: '/school/financialaid/staff' },
      { pattern: '/school/financialaid/staff/:applicationId/:tab' },
    ],
    observedOperations: [
      appEndpoint('listFinancialAidApplicationsForReview', 'dashboard.queue'),
      appEndpoint('getSettingsModuleConfig', 'dashboard.settings'),
      appEndpoint('getFinancialAidApplicationForReview', 'detail.review'),
      appEndpoint('getFinancialAidStatusHistory', 'detail.statusHistory'),
    ],
    treeProjector: ({ read }) => ({
      staff: {
        dashboard: {
          get queue() {
            return read(appEndpoint('listFinancialAidApplicationsForReview', 'dashboard.queue'), undefined);
          },
        },
        detail: new Proxy({}, {
          get(_target, property) {
            const applicationId = String(property);
            return {
              get review() {
                return read(appEndpoint('getFinancialAidApplicationForReview', 'detail.review'), applicationId);
              },
              get statusHistory() {
                return read(appEndpoint('getFinancialAidStatusHistory', 'detail.statusHistory'), applicationId);
              },
            };
          },
        }),
      },
    }),
  }),
  defineRtkFetchSideLanePolicy({
    policyId: 'ystnj-tuition-staff-rtk-fetch-side-lane-v1',
    routeFamily: 'tuition',
    staffRoutes: [
      { routeId: 'tuition-staff-dashboard' },
      { routeId: 'tuition-staff-family-ledger' },
      { pattern: '/school/tuition' },
      { pattern: '/school/tuition/family/:familyId' },
    ],
    observedOperations: [
      appEndpoint('getSchoolTuitionDashboardTree', 'dashboard.snapshot'),
      appEndpoint('getSchoolTuitionFamilyLedgerTree', 'familyLedger.snapshot'),
    ],
    treeProjector: ({ read }) => ({
      staff: {
        dashboard: {
          snapshot(args: unknown) {
            return read(appEndpoint('getSchoolTuitionDashboardTree', 'dashboard.snapshot'), args);
          },
        },
        familyLedger: new Proxy({}, {
          get(_target, property) {
            const familyId = String(property);
            return {
              snapshot(args: { academicYear: string }) {
                return read(appEndpoint('getSchoolTuitionFamilyLedgerTree', 'familyLedger.snapshot'), {
                  ...args,
                  familyId,
                });
              },
            };
          },
        }),
      },
    }),
  }),
] as const;

const upsertDomainQueryData = domainRouterUniversalApi.util.upsertQueryData as unknown as (
  endpointName: 'domainQuery',
  args: DomainQueryRequest,
  payload: unknown,
) => unknown;

const domainRouterEndpoints = domainRouterApi.endpoints as Record<
  string,
  { select: (args: unknown) => (state: unknown) => unknown }
>;

const upsertDomainRouterQueryData = domainRouterApi.util.upsertQueryData as unknown as (
  endpointName: string,
  args: unknown,
  payload: unknown,
) => unknown;

const apiAdapters: Record<string, RtkFetchSideLaneApiAdapter> = {
  [domainRouterApi.reducerPath]: {
    select: (endpointName, args) => {
      const endpoint = domainRouterEndpoints[endpointName];
      return endpoint.select(args);
    },
    upsertQueryData: (endpointName, args, payload) =>
      upsertDomainRouterQueryData(endpointName, args, payload),
  },
  [domainRouterUniversalApi.reducerPath]: {
    select: (_endpointName, args) =>
      domainRouterUniversalApi.endpoints.domainQuery.select(args as DomainQueryRequest) as unknown as (
        state: unknown,
      ) => unknown,
    upsertQueryData: (_endpointName, args, payload) =>
      upsertDomainQueryData('domainQuery', args as DomainQueryRequest, payload),
  },
};

export const ystnjRtkFetchSideLaneReducer = rtkFetchSideLaneReducer;

export const ystnjRtkFetchSideLaneMiddleware = createRtkFetchSideLaneMiddleware({
  mode: resolveMode(),
  policies: ystnjRtkFetchSideLanePolicies,
  routeSelector: resolveYstnjRtkFetchSideLaneRoute,
  instrumentation: {
    record: recordEvent,
  },
  apiAdapters,
});

export const createYstnjRtkFetchSideLaneProxy = (store: StoreLike) =>
  createRtkFetchSideLaneProxy({
    policies: ystnjRtkFetchSideLanePolicies,
    apiAdapters,
    getState: store.getState,
  });
